/**
 * Typed GraphQL reads for a place's schedule (place manager, unit 6).
 * Read-only: nothing in this module writes.
 *
 * Next up comes through the place's seasons, as how the last ones went does
 * (`place-events-api.ts`: `Place.seasons` → `Season.events`, the same page
 * read): `Season.events` lists a season's events newest first by start with
 * no bound on time, so the events still to come are its first rows. One more
 * read, {@link UpcomingDetailsQuery}, asks `Query.event` for the game and the
 * rules preset of just the events shown, and re-checks that this viewer may
 * see each (both apply the same visibility rule). That read keeps a partial
 * answer: one event failing never fails the other nine.
 *
 * Repeats (`scheduledEvents`) and templates (`eventTemplates`) are admin-only:
 * they resolve only through the staff door (a super admin's call, see
 * `request-headers.ts`). `schedule.ts` never asks for them for anyone else.
 * Neither has a place of its own: a template names its place (or season) in
 * its JSON, and a repeat makes its events from a template.
 *
 * `place-events-api.test.ts` keeps each read under the backend's query cost limit.
 */

import {
  GraphQLRequestError,
  graphql,
  gqlRequestAs,
  gqlRequestPartialAs,
  type GraphQLErrorEntry,
  type RequestOptions,
  type ResultOf,
} from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

/** What a Next up row adds to the listed event: its game and its rules preset. */
export const UpcomingEventFields = graphql(`
  fragment UpcomingEventFields on Event {
    id
    gatheringActivity {
      name
    }
    gatheringSettings {
      name
    }
  }
`);

/** One upcoming event's game and rules preset, as the details read returns them. */
export type UpcomingEventData = ResultOf<typeof UpcomingEventFields>;

/** The most events one details read asks for (`NEXT_UP_SHOWN` in
 *  `schedule.ts`, tied by a test): one alias each, `u0` always, the others
 *  only when `withN` is true. */
export const UPCOMING_PER_READ = 10;

export const UpcomingDetailsQuery = graphql(
  `
  query UpcomingDetails(
    $u0: String!
    $u1: String!
    $u2: String!
    $u3: String!
    $u4: String!
    $u5: String!
    $u6: String!
    $u7: String!
    $u8: String!
    $u9: String!
    $with1: Boolean!
    $with2: Boolean!
    $with3: Boolean!
    $with4: Boolean!
    $with5: Boolean!
    $with6: Boolean!
    $with7: Boolean!
    $with8: Boolean!
    $with9: Boolean!
  ) {
    u0: event(id: $u0) {
      ...UpcomingEventFields
    }
    u1: event(id: $u1) @include(if: $with1) {
      ...UpcomingEventFields
    }
    u2: event(id: $u2) @include(if: $with2) {
      ...UpcomingEventFields
    }
    u3: event(id: $u3) @include(if: $with3) {
      ...UpcomingEventFields
    }
    u4: event(id: $u4) @include(if: $with4) {
      ...UpcomingEventFields
    }
    u5: event(id: $u5) @include(if: $with5) {
      ...UpcomingEventFields
    }
    u6: event(id: $u6) @include(if: $with6) {
      ...UpcomingEventFields
    }
    u7: event(id: $u7) @include(if: $with7) {
      ...UpcomingEventFields
    }
    u8: event(id: $u8) @include(if: $with8) {
      ...UpcomingEventFields
    }
    u9: event(id: $u9) @include(if: $with9) {
      ...UpcomingEventFields
    }
  }
`,
  [UpcomingEventFields],
);

/** One upcoming event's details as read: the details; null when the event
 *  does not exist or the caller cannot see it; `unread` when the event was
 *  found but a field under it failed (`Event.gatheringActivity` and
 *  `gatheringSettings` throw when the linked record is missing), so it is
 *  visible and its details are not; `failed` when its lookup itself failed, so
 *  whether the caller may see it is not known. */
export type UpcomingDetail = UpcomingEventData | null | 'unread' | 'failed';

/** Each alias's outcome from a partial answer (`u0` … in order, `n` of them),
 *  so one failing alias never fails the others. An error not under an alias
 *  fails the whole read. */
export function upcomingDetailsOf(
  data: Record<string, UpcomingEventData | null | undefined>,
  errors: readonly GraphQLErrorEntry[],
  n: number,
): UpcomingDetail[] {
  const aliases = Array.from({ length: n }, (_, i) => `u${i}`);
  const stray = errors.filter((e) => !e.path || !aliases.includes(String(e.path[0])));
  if (stray.length > 0) throw GraphQLRequestError.from(stray);
  return aliases.map((alias) => {
    const own = errors.filter((e) => e.path![0] === alias);
    if (own.some((e) => e.path!.length === 1)) return 'failed';
    if (own.length > 0) return 'unread';
    return data[alias] ?? null;
  });
}

/** Several upcoming events' details in one read, by public id, in the order
 *  asked (see {@link UpcomingDetail}). At most {@link UPCOMING_PER_READ} ids. */
export async function fetchUpcomingDetails(
  eventIds: readonly string[],
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<UpcomingDetail[]> {
  if (eventIds.length === 0) return [];
  if (eventIds.length > UPCOMING_PER_READ) throw new Error(`At most ${UPCOMING_PER_READ} events per details read.`);
  const [u0, u1 = u0, u2 = u0, u3 = u0, u4 = u0, u5 = u0, u6 = u0, u7 = u0, u8 = u0, u9 = u0] = eventIds as [string, ...string[]];
  const n = eventIds.length;
  const { data, errors } = await gqlRequestPartialAs(
    viewer,
    UpcomingDetailsQuery,
    {
      u0, u1, u2, u3, u4, u5, u6, u7, u8, u9,
      with1: n > 1, with2: n > 2, with3: n > 3, with4: n > 4,
      with5: n > 5, with6: n > 6, with7: n > 7, with8: n > 8, with9: n > 9,
    },
    options,
  );
  return upcomingDetailsOf(data, errors, n);
}

/** Every template not archived, by sort order. Admin-only (staff door).
 *  `first: 100` is `TEMPLATES_ASKED` in `schedule.ts` (tied by a test); the
 *  backend's resolver does not apply it today (it sends every template, up to
 *  9,999), so it only sets the read's cost. `templateJson` is the template's whole shape as a JSON
 *  string; `schedule.ts` reads the keys it needs from it. `defaultDuration` is
 *  how long events made from it run, in ms: 45 minutes when never set, and the
 *  admin GUI's "new event from template" uses that value as it reads. */
export const EventTemplatesQuery = graphql(`
  query EventTemplates {
    eventTemplates(first: 100, includeArchived: false) {
      id
      name
      defaultDuration
      templateJson
    }
  }
`);

/** One template, as the templates read returns it. */
export type EventTemplateData = ResultOf<typeof EventTemplatesQuery>['eventTemplates'][number];

/** The templates. Only ever called for a super admin (see `schedule.ts`). */
export async function fetchEventTemplates(viewer: RequestViewer, options?: RequestOptions): Promise<EventTemplateData[]> {
  const data = await gqlRequestAs(viewer, EventTemplatesQuery, undefined, options);
  return data.eventTemplates;
}

/** Every repeat (a scheduled event: the backend makes an event from its
 *  template on the days and at the time it names, in its own time zone,
 *  running `eventDurationInMs`). The field takes no arguments, so this is
 *  every one. Admin-only (staff door). */
export const ScheduledEventsQuery = graphql(`
  query ScheduledEvents {
    scheduledEvents {
      totalCount
      nodes {
        id
        name
        startEventOnDays
        eventStartTimeHour
        eventStartTimeMinute
        timeZone
        eventDurationInMs
        nextEventToBeCreatedAt
        eventTemplate {
          id
          name
          templateJson
        }
      }
    }
  }
`);

/** One repeat, as the repeats read returns it. */
export type ScheduledEventData = ResultOf<typeof ScheduledEventsQuery>['scheduledEvents']['nodes'][number];

/** The repeats. Only ever called for a super admin (see `schedule.ts`). */
export async function fetchScheduledEvents(viewer: RequestViewer, options?: RequestOptions): Promise<ScheduledEventData[]> {
  const data = await gqlRequestAs(viewer, ScheduledEventsQuery, undefined, options);
  return data.scheduledEvents.nodes;
}
